// useNarrowViewport — subscribed hook for the v2 chrome's narrow-viewport
// breakpoint (≤ 768px, the conventional sm/md boundary).
//
// CHG-1427 / ITER-3547 — created for DRIFT-657 (sidebar phone overlay mode).
// Subscribes to matchMedia "change" events so the Sidebar component can react
// to viewport changes (rotation, resize) — not just mount-time state.
//
// Distinct from CHG-1422's `detectNarrowViewport()` helper in AppShell.tsx,
// which is MOUNT-ONLY (lazy useState initializer) and drives the initial
// collapsed state. That mount-only path deliberately does NOT subscribe to
// changes — auto-collapsing after the user explicitly expanded would be too
// magical. This hook is a separate concern: it drives the RENDERED behavior
// of Sidebar (overlay vs sticky positioning, width 0 vs 64), which should
// react to viewport changes without changing the user's collapsed choice.
//
// SSR-safe: returns false when window or matchMedia is undefined so server
// render matches the desktop default.

import { useEffect, useState } from "react";

export const NARROW_VIEWPORT_QUERY = "(max-width: 768px)";

function matchesNarrowSync(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(NARROW_VIEWPORT_QUERY).matches;
}

export function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState<boolean>(matchesNarrowSync);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(NARROW_VIEWPORT_QUERY);
    // Re-sync on mount in case the initial useState value was captured before
    // matchMedia was available (rare; SSR-then-hydrate path).
    setNarrow(mql.matches);
    const handler = (ev: MediaQueryListEvent) => setNarrow(ev.matches);
    // `addEventListener` is the modern API; addListener is the deprecated
    // pre-2020 fallback. Both exist on MediaQueryList in jsdom + all browsers.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    } else {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
  }, []);

  return narrow;
}
